import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, LogOut, MoreVertical, Settings, User } from 'lucide-react';
import { useHospital } from '@/hooks/useHospital';
import HospitalIcon from '@/assets/Image/HospitalIcon.png';
import ArrowDropDownIcon from '@/assets/Svg/arrow_drop_down.svg';
import Typography from '@/components/ui/typography';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { navigationItems } from '@/constants/constant';
import { TOAST_MESSAGES, useToastHelpers } from '@/utils/toast';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/store';
import { clearHospitals } from '@/store/hospitalSlice';
import { endBrokerSession } from '@/services/auth/brokerSession';

import {
  logout as logoutUser,
  changePassword,
  clearChangePasswordState,
} from '@/store/userSlice';
import ProfilePopup, { UserProfile } from '../ui/ProfilePopup';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { usePermissions } from '@/hooks';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  currentPage = 'Dashboard',
}) => {
  const {
    selectedHospital,
    hospitals,
    updateSelectedHospital,
    loading,
    switchingTenant,
    userVerificationLoading,
    hasMultipleHospitals,
  } = useHospital();

  // The tenant picker is busy from the click through the token switch and the
  // verify that follows; disable it for that whole window.
  const tenantSwitchBusy = switchingTenant || userVerificationLoading;

  //changes
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showError } = useToastHelpers();

  const { logout } = useAuth0();
  const { user } = useSelector((state: RootState) => state.user);

  const onLogoutClick = async () => {
    // Revoke the broker session(s) first — this reads the stored refresh tokens,
    // so it must run before localStorage is cleared. Best-effort; never blocks.
    await endBrokerSession();

    // Clear all localStorage items
    localStorage.clear();

    // Clear all sessionStorage items
    sessionStorage.clear();

    // Clear Redux store data
    dispatch(clearHospitals());
    dispatch(logoutUser());

    // End the Auth0 session with proper return URL based on environment
    logout({
      logoutParams: {
        returnTo: window.location.origin + '/login',
      },
    });
  };

  const handleProfileClick = () => {
    setIsProfileOpen(true);
    // onProfileClick?.();
  };

  const handleProfileClose = () => {
    setIsProfileOpen(false);
  };

  const handleChangePassword = async () => {
    // Clear any previous state
    dispatch(clearChangePasswordState());

    try {
      if (!user?.email) {
        showError('Email not found. Please try again.');
        return;
      }

      const res = await dispatch(
        changePassword({ email: user.email }),
      ).unwrap();

      // Show message returned by API
      showSuccess(res.message || TOAST_MESSAGES.PASSWORD_RESET_SUCCESS);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      showError(`${TOAST_MESSAGES.PASSWORD_RESET_ERROR}: ${errorMessage}`);
    }
  };

  const truncateText = useCallback((text: string, maxLength: number = 15) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.slice(0, maxLength) + '...';
  }, []);

  const handleHospitalSelect = useCallback(
    async (hospitalName: string) => {
      try {
        await updateSelectedHospital(hospitalName);
      } catch {
        // The broker token switch failed (network / access). Leave the previous
        // tenant selected and active rather than publishing a tenant the token
        // isn't scoped to.
        showError('Failed to switch hospital. Please try again.');
      }
    },
    [updateSelectedHospital, showError],
  );

  const { isAdmin: isAdminUser } = usePermissions();
  const canViewDataExtraction = useMemo(() => {
    const email = user?.email?.toLowerCase().trim();
    if (!email || !email.includes('@')) {
      return false;
    }

    const domain = email.split('@')[1];
    return domain === 'mindbowser.com' || domain === 'usemaia.com';
  }, [user?.email]);

  // Memoize navigation items to prevent unnecessary re-renders
  const navItems = useMemo(
    () => navigationItems(currentPage, isAdminUser(), canViewDataExtraction),
    [currentPage, isAdminUser, canViewDataExtraction],
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-screen bg-gray-500 dark:bg-gray-900 overflow-hidden">
        <Sidebar className="border-r-0 bg-neutral-50 flex-shrink-0 w-64">
          <SidebarHeader className="border-b-0 dark:border-gray-700 ">
            <div className="p-2">
              {loading ? (
                <div className="flex items-center gap-2 py-4 px-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white-100">
                    <img
                      src={HospitalIcon}
                      alt="Hospital"
                      className="h-8 w-8"
                    />
                  </div>
                  <div className="flex flex-col items-start min-w-0  flex-1">
                    <div className="animate-pulse bg-gray-200 h-4 w-24 rounded"></div>
                  </div>
                </div>
              ) : hasMultipleHospitals ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between py-4 px-2 h-auto bg-gray-50"
                      disabled={tenantSwitchBusy}
                    >
                      <div className="flex items-center gap-2 ">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white-100">
                          <img
                            src={HospitalIcon}
                            alt="Hospital"
                            className="h-8 w-8"
                          />
                        </div>
                        <div className="flex flex-col items-start min-w-0 flex-1 ">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Typography
                                  variant="p"
                                  color="neutral900"
                                  weight="normal"
                                  size="14"
                                >
                                  {userVerificationLoading
                                    ? 'Verifying access...'
                                    : switchingTenant
                                      ? 'Switching…'
                                      : truncateText(selectedHospital)}
                                </Typography>
                              </TooltipTrigger>
                              <TooltipContent>
                                <Typography
                                  variant="small"
                                  className="text-white truncate text-xs sm:text-sm cursor-help min-w-0"
                                >
                                  {selectedHospital}
                                </Typography>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                      <img
                        src={ArrowDropDownIcon}
                        alt="Dropdown"
                        className="h-6 w-6"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64" align="start">
                    <div className="px-2 py-1.5">
                      <Typography
                        variant="p"
                        color="neutral900"
                        weight="semibold"
                        size="14"
                      >
                        Select Hospital
                      </Typography>
                    </div>
                    <DropdownMenuSeparator />
                    {hospitals.map((hospital) => (
                      <DropdownMenuItem
                        key={hospital.tenantId}
                        onClick={() => handleHospitalSelect(hospital.name)}
                        className="flex flex-col items-start p-3 cursor-pointer"
                        disabled={tenantSwitchBusy}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <div className="flex h-6 w-6 items-center justify-center rounded bg-white-100 dark:bg-purple-900">
                            <img
                              src={HospitalIcon}
                              alt="Hospital"
                              className="h-3 w-3"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Typography
                                      variant="small"
                                      className="text-neutral-700 truncate text-xs sm:text-sm cursor-help min-w-0"
                                    >
                                      {hospital.name}
                                    </Typography>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <Typography
                                      variant="small"
                                      className="text-white truncate text-xs sm:text-sm cursor-help min-w-0"
                                    >
                                      {hospital.name}
                                    </Typography>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {selectedHospital === hospital.name && (
                                <Check className="h-3 w-3 text-black-600 " />
                              )}
                            </div>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                // Single hospital display (no dropdown) - button style
                <Button
                  variant="outline"
                  className="w-full justify-between py-4 px-2 h-auto cursor-default"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white-100">
                      <img
                        src={HospitalIcon}
                        alt="Hospital"
                        className="h-8 w-8"
                      />
                    </div>
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Typography
                              variant="p"
                              color="neutral900"
                              weight="normal"
                              size="14"
                            >
                              {truncateText(selectedHospital)}
                            </Typography>
                          </TooltipTrigger>
                          <TooltipContent>
                            <Typography
                              variant="small"
                              className="text-white truncate text-xs sm:text-sm cursor-help min-w-0"
                            >
                              {selectedHospital}
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </Button>
              )}
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems.map((item) => (
                    <SidebarMenuItem
                      key={item.title}
                      className="m-1"
                      style={{ display: item.isVisible ? 'block' : 'none' }}
                    >
                      <SidebarMenuButton
                        asChild
                        isActive={item.isActive}
                        tooltip={item.title}
                        className={
                          item.isActive
                            ? '!bg-white !text-black data-[active=true]:!bg-white data-[active=true]:!text-black border border-neutral-200 h-10'
                            : 'h-10 text-gray-600'
                        }
                      >
                        <Link to={item.href}>
                          <img
                            src={item.icon}
                            alt={item.title}
                            className={`h-4 w-4 ${item.isActive ? 'brightness-0' : 'opacity-70'}`}
                          />
                          <Typography
                            variant="p"
                            color={item.isActive ? 'black' : 'neutral500'}
                            weight={item.isActive ? 'semibold' : 'normal'}
                            size="16"
                          >
                            {item.title}
                          </Typography>
                        </Link>
                      </SidebarMenuButton>
                      {item.children?.length ? (
                        <SidebarMenuSub className="mx-0 border-l border-neutral-200 pl-3 ml-3 mt-1">
                          {item.children.map((child) => (
                            <SidebarMenuSubItem
                              key={child.title}
                              style={{
                                display: child.isVisible ? 'block' : 'none',
                              }}
                            >
                              <SidebarMenuSubButton
                                asChild
                                isActive={child.isActive}
                                size="md"
                                className={
                                  child.isActive
                                    ? '!bg-white !text-black h-9 border border-dashed border-neutral-300 data-[active=true]:!bg-white'
                                    : 'h-9 text-gray-600'
                                }
                              >
                                <Link to={child.href}>
                                  <Typography
                                    variant="p"
                                    color={
                                      child.isActive ? 'black' : 'neutral500'
                                    }
                                    weight={
                                      child.isActive ? 'semibold' : 'normal'
                                    }
                                    size="14"
                                  >
                                    {child.title}
                                  </Typography>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      ) : null}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          {/* Sidebar Footer */}
          <div className="mt-auto  mb-10 border-neutral-200">
            {/* User Profile Section */}
            <div className="border-t border-neutral-200 -mr-2">
              <div className="flex items-center justify-center py-4 px-2">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      alt={
                        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                        user?.email?.split('@')[0] ||
                        'User'
                      }
                    />
                    <AvatarFallback
                      className="bg-[#5536F5] text-white font-medium text-lg rounded-full border-0 flex items-center justify-center"
                      style={{
                        height: '35px',
                        width: '35px',
                        borderRadius: '50%',
                      }}
                    >
                      {user?.firstName?.charAt(0) ||
                        user?.email?.charAt(0) ||
                        'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0 flex-1">
                    <Typography
                      variant="p"
                      color="neutral900"
                      weight="semibold"
                      size="16"
                      className="truncate"
                    >
                      {`${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                        user?.email?.split('@')[0] ||
                        'User'}
                    </Typography>
                    <Typography
                      variant="p"
                      color="neutral500"
                      weight="normal"
                      size="14"
                      className="truncate"
                    >
                      {user?.email || 'user@example.com'}
                    </Typography>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="h-8 w-8 p-0 rounded transition-colors flex items-center justify-center text-neutral-500 hover:bg-primary-base/5 hover:text-primary-base">
                      <MoreVertical className="h-5 w-5 cursor-pointer" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="text-xs text-neutral-500">
                      Version {__APP_VERSION__}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleProfileClick}
                      className="hover:bg-primary-base/5 focus:bg-primary-base/5"
                    >
                      <User className="mr-2 h-4 w-4" />
                      <Typography
                        variant="p"
                        color="neutral900"
                        weight="normal"
                        size="14"
                      >
                        Profile
                      </Typography>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleChangePassword}
                      className="hover:bg-primary-base/5 focus:bg-primary-base/5"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      <Typography
                        variant="p"
                        color="neutral900"
                        weight="normal"
                        size="14"
                      >
                        Change password
                      </Typography>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={onLogoutClick}
                      className="text-red-600 hover:bg-red-50 focus:bg-red-50"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <Typography
                        variant="p"
                        color="destructive"
                        weight="normal"
                        size="14"
                      >
                        Log out
                      </Typography>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Profile Popup */}
          {user && (
            <ProfilePopup
              user={user as UserProfile}
              isOpen={isProfileOpen}
              onClose={handleProfileClose}
            />
          )}
        </Sidebar>
        <SidebarInset className="flex-1 min-w-0">
          {/* Main Content Area */}
          <main className="flex-1 overflow-hidden bg-gray-50 w-full h-screen min-w-0">
            <div className="w-full h-full p-4 lg:p-4 pt-16 lg:pt-4 overflow-auto min-w-0">
              <div className="space-y-6 bg-white rounded-lg p-2 border border-gray-200 h-full min-w-0 overflow-auto">
                {children}
              </div>
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default AppLayout;
